import { HttpClient, provideHttpClient, withInterceptors } from "@angular/common/http";
import { ApplicationConfig, importProvidersFrom, isDevMode } from "@angular/core";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideRouter, withComponentInputBinding } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import {
  B2bCatalogService,
  B2bSessionService,
  ConfigService,
  b2bAuthInterceptor,
  httpCacheInterceptor,
  provideB2bSessionInitializer,
} from "b2b-ecommerce-lib";
import { NgcCookieConsentConfig, provideNgcCookieConsent } from "ngx-cookieconsent";
import { MessageService } from "primeng/api";
import { providePrimeNG } from "primeng/config";
import { DialogService } from "primeng/dynamicdialog";
import { Environment } from "../environments/environment";
import { AppConfigService } from "./app-config.service";
import { routes } from "./app.routes";
import { pendingInterceptor } from "./interceptors/pending.interceptor";
import SapientiaTheme from "./sapientia-theme.config";
import { CartService } from "./services/cart.service";
import { CatalogService } from "./services/catalog.service";
import { SessionService } from "./services/login.service";
import { PaymentService } from "./services/payment.service";

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

export function createCookieConfig(): NgcCookieConsentConfig {
  const getCookieDomain = (): string => {
    const hostname = window.location.hostname;
    console.log("Hostname:", hostname);

    // localhost and variants
    if (hostname === "localhost" || hostname.endsWith(".localhost")) {
      return "localhost";
    }

    // Dev and Test envs (*.digitalbusinessolution.com)
    if (hostname.endsWith("digitalbusinessolution.com")) {
      return "digitalbusinessolution.com";
    }

    // Prod (*.sapientia.store)
    if (hostname.endsWith("sapientia.store")) {
      return "sapientia.store";
    }

    return Environment.CONFIG.production ? "sapientia.store" : "localhost";
  };

  return {
    cookie: {
      domain: getCookieDomain(),
      secure: typeof window !== "undefined" && window.location.protocol === "https:",
    },
    palette: {
      popup: {
        background: "#2d3648",
      },
      button: {
        background: "#ffc77e",
      },
    },
    theme: "block",
    type: "info",
    content: {
      message:
        "Questo sito Web utilizza i cookies per fornire il miglior servizio possibile ai nostri visitatori e utenti. I cookies sono impiegati per il corretto funzionamento del sito, per migliorare la user experience, per monitorare il traffico dei visitatori e per finalità di marketing. Continuando a navigare sul sito, accetti il nostro impiego dei cookies. Puoi trovare maggiori informazioni sui cookies",
      link: "Qui",
      href: "/privacy",
      dismiss: "Ok",
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    DialogService,
    provideRouter(routes, withComponentInputBinding()),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptors([b2bAuthInterceptor, httpCacheInterceptor, pendingInterceptor]),
    ),
    provideB2bSessionInitializer(),
    provideNgcCookieConsent(createCookieConfig()),
    providePrimeNG({
      theme: {
        preset: SapientiaTheme,
        options: {
          darkModeSelector: ".dark",
          cssLayer: {
            name: "components",
          },
        },
      },
    }),
    {
      provide: "env",
      useValue: Environment.CONFIG,
    },
    { provide: ConfigService, useClass: AppConfigService },
    MessageService,
    {
      provide: B2bSessionService,
      useExisting: SessionService,
    },
    {
      provide: PaymentService,
      useExisting: CartService,
    },
    {
      provide: B2bCatalogService,
      useExisting: CatalogService,
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
    ),
    provideServiceWorker("ngsw-worker.js", {
      enabled: !isDevMode(),
      registrationStrategy: "registerImmediately",
    }),
  ],
};
